import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, ChatList } from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';

// import Support from '../Support/Conversation';
import CardLoader from '../../design-system/loading/card/CardLoader';
import { UserTypes } from '../../../domains/user/user-types';
import { cleanConversation, getChatConversations, joinConversation, setIsSupport, setSelectedConversation } from '../../../actions/chat';

import styles from './index.module.scss';
import ChatterboxLogo from '../../../assets/logos/chatterbox-logo.png';

const TABS = Object.freeze({
  all: 'All',
  coach: 'Coach',
  languageTeacher: 'Language Helper'
});

const Conversations = ({
  cleanConversationAction,
  Conversations,
  getChatConversationsAction,
  isLoadingConversations,
  isMobile,
  // isSupport,
  joinConversationAction,
  setIsSupportAction,
  setSelectedConversationAction,
  user,
  chatId
}) => {
  const [activeButton, setActiveButton] = useState(TABS.all);
  const [selectedConversationIndex, setSelectedConversationIndex] = useState(isMobile ? null : 0);
  const [isDumbLoading, setIsDumbLoading] = useState(false);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    console.log('🚀 Component mounted, getting conversations...');
    getChatConversationsAction(user);

    return () => {
      if (!isMobile) cleanConversationAction();
    };
  }, []);

  // select the conversation based on chatId prop
  useEffect(() => {
    if (chatId && filteredConversations.length > 0) {
      const selectedConv = filteredConversations.findIndex((conv) => conv.channelState?.uniqueName === chatId);
      if (selectedConv) {
        handleConversationSelection(selectedConv);
      }
    }
  }, [chatId, filteredConversations]);

  const triggerDumbLoading = () => {
    setIsDumbLoading(true);
    setTimeout(() => {
      setIsDumbLoading(false);
    }, 400);
  };

  const setSelectedConversation = (selectedConversation) => {
    if (!selectedConversation) return;
    setSelectedConversationAction(selectedConversation);
  };

  const searchConversations = (conversations, query) => {
    if (!query || query.trim() === '') return conversations;

    const lowerQuery = query.toLowerCase();
    return conversations.filter((conv) => {
      // Search in conversation friendly name
      if (conv.friendlyName?.toLowerCase().includes(lowerQuery)) {
        return true;
      }

      // Search in participant names
      const participants = conv.attributes?.participantNames || [];
      for (const participant of participants) {
        if (participant?.name?.toLowerCase().includes(lowerQuery)) {
          return true;
        }
        if (participant?.email?.toLowerCase().includes(lowerQuery)) {
          return true;
        }
      }

      return false;
    });
  };

  useEffect(() => {
    let filtered = Conversations;

    // First apply tab filter
    if (activeButton === TABS.coach) {
      filtered = Conversations.filter((c) => !c?.attributes?.courseLeaderChat);
    } else if (activeButton === TABS.languageTeacher) {
      filtered = Conversations.filter((c) => c?.attributes?.courseLeaderChat);
    }

    // Then apply search filter
    filtered = searchConversations(filtered, searchQuery);

    // Only trigger loading for tab changes, not search
    if (!searchQuery) {
      triggerDumbLoading();
    }

    setFilteredConversations(filtered);

    if (!isMobile && filtered.length > 0) {
      setSelectedConversationIndex(0);
      setSelectedConversation(filtered[0]);
    }
  }, [activeButton, searchQuery, Conversations]);

  const getSubtitle = (conversation) => {
    const participants = conversation?.attributes?.participantNames;
    if (!participants?.length) return '';

    const substring = participants.reduce((names, p, index) => {
      if (p?.email === user?.email) return names;
      if (index === participants.length - 1) return names + p?.name;
      return names + p?.name + ', ';
    }, 'With ');

    if (!conversation?.attributes?.courseLeaderChat) return <p className="coachChat">{substring}</p>;
    return (
      <>
        <p>{substring}</p>
        <div className={styles.languageHelperTag}>Language Helper</div>
      </>
    );
  };

  const handleConversationSelection = async (index) => {
    if (typeof index !== 'number') return;
    console.log(`🎯 Selected conversation ${index}: ${filteredConversations[index]?.friendlyName}`);
    setSelectedConversationIndex(index);
    if (!filteredConversations[index]) return;
    const selectedConv = filteredConversations[index];
    setSelectedConversation(selectedConv);
    joinConversationAction(selectedConv);
    setIsSupportAction(false);

    // Mark all messages as read when selecting a conversation
    try {
      await selectedConv.setAllMessagesRead();
      console.log(`✅ Marked all messages as read for: ${selectedConv.friendlyName}`);

      // Update the unread count for this specific conversation immediately
      setUnreadCounts((prev) => ({
        ...prev,
        [selectedConv.sid]: 0
      }));

      // Don't need to refresh - the periodic refresh will catch any changes
      // and we've already updated the count locally
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  // Fetch unread counts for all conversations
  useEffect(() => {
    console.log('🔄 filteredConversations changed, fetching unread counts...');
    const fetchUnreadCounts = async () => {
      const counts = {};
      for (const conv of filteredConversations) {
        try {
          const count = await conv.getUnreadMessagesCount();
          counts[conv.sid] = count || 0;
          // Only log if there are unread messages
          if (count > 0) {
            console.log(`📬 Chat "${conv.friendlyName}": ${count} unread messages`);
          }
        } catch (err) {
          console.error(`Error getting unread count for ${conv.friendlyName}:`, err);
          counts[conv.sid] = 0;
        }
      }
      setUnreadCounts(counts);
    };

    if (filteredConversations.length > 0) {
      fetchUnreadCounts();
    }
  }, [filteredConversations]);

  const adaptConversationToChannel = (conversations) => {
    if (!conversations) return [];
    return conversations.map((conversation, index) => {
      const unread = unreadCounts[conversation.sid] || 0;

      return {
        avatar: conversation.attributes?.image || ChatterboxLogo,
        alt: conversation.friendlyName,
        title: conversation.friendlyName,
        subtitle: getSubtitle(conversation),
        date: conversation?.lastMessage?.dateCreated || conversation?.dateUpdated,
        unread,
        object: conversation,
        index,
        className: [
          styles.conversationBox,
          index === selectedConversationIndex && styles.conversationBoxSelected,
          unread > 0 && styles.conversationBoxUnread
        ]
          .filter(Boolean)
          .join(' ')
      };
    });
  };

  // const openSupportChat = () => {
  //   setSelectedConversation({});
  //   setSelectedConversationIndex(null);
  //   setIsSupportAction(true);
  // };

  const Buttons = user.user_type === UserTypes.student && (
    <div className={styles.chatNav}>
      {Object.keys(TABS).map((tab) => (
        <Button
          key={tab}
          className={activeButton === TABS[tab] ? [styles.navButton, styles.selectedNavButton].join(' ') : styles.navButton}
          text={TABS[tab]}
          onClick={() => setActiveButton(TABS[tab])}
        />
      ))}
    </div>
  );

  if (isLoadingConversations || isDumbLoading)
    return (
      <div className={styles.containerWrapper}>
        {Buttons}
        <div className={styles.loadingWrapper}>
          {new Array(4).fill(1).map((_, index) => (
            <CardLoader key={index} rows={3} rowsColor="gray" />
          ))}
        </div>
      </div>
    );

  if (!Conversations || !Conversations.length)
    return (
      <div className={styles.containerWrapper}>
        <p className={styles.noChannelsMsg}>
          Something went wrong. Try to log out and log in again. If this error persists, please contact us at{' '}
          <a href="mailto:support@chatterbox.io">support@chatterbox.io</a>
        </p>
      </div>
    );

  return (
    <div className={styles.containerWrapper}>
      {Buttons}
      <div className={styles.searchWrapper}>
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
        {searchQuery && (
          <button className={styles.clearButton} onClick={() => setSearchQuery('')} aria-label="Clear search">
            ×
          </button>
        )}
      </div>
      {filteredConversations.length === 0 && searchQuery ? (
        <div className={styles.noSearchResults}>
          <p>No conversations found matching &ldquo;{searchQuery}&rdquo;</p>
        </div>
      ) : (
        <ChatList
          className={styles.chatList}
          dataSource={adaptConversationToChannel(filteredConversations)}
          onClick={({ index }) => handleConversationSelection(index)}
        />
      )}
      {/* <Support active={isSupport} onClick={openSupportChat} /> */}
    </div>
  );
};

Conversations.propTypes = {
  cleanConversationAction: PropTypes.func.isRequired,
  Conversations: PropTypes.array.isRequired,
  getChatConversationsAction: PropTypes.func.isRequired,
  isLoadingConversations: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSupport: PropTypes.bool,
  joinConversationAction: PropTypes.func.isRequired,
  setIsSupportAction: PropTypes.func.isRequired,
  setSelectedConversationAction: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    user_type: PropTypes.string.isRequired
  }),
  chatId: PropTypes.string
};

const mapStateToProps = (state) => ({
  Conversations: state.chat?.conversations?.Conversations,
  isLoadingConversations: state.chat?.conversations?.isLoadingConversations,
  isSupport: state.chat?.conversations?.isSupport,
  user: state.user
});

export default connect(mapStateToProps, {
  cleanConversationAction: cleanConversation,
  getChatConversationsAction: getChatConversations,
  joinConversationAction: joinConversation,
  setIsSupportAction: setIsSupport,
  setSelectedConversationAction: setSelectedConversation
})(Conversations);
